"use client";

import { useEffect } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useAuth } from "@/context/AuthContext";
import { useNativePushRegistration } from "@/hooks/useNativePushRegistration";
import { isNativeApp } from "@/lib/nativePlatform";
import {
  getRememberedNativeRoute,
  rememberCurrentNativeRoute,
  rememberNativeRoute,
  routeNativePath,
} from "@/lib/nativeRouting";

export default function NativeBridgeBootstrap() {
  const { user } = useAuth();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();

  useNativePushRegistration({ user });

  useEffect(() => {
    if (!isNativeApp()) return;
    const query = searchParams?.toString();
    rememberNativeRoute(query ? `${pathname || "/"}?${query}` : pathname || "/");
  }, [pathname, searchParams]);

  useEffect(() => {
    if (!isNativeApp()) return;

    let cancelled = false;
    const handles = [];

    (async () => {
      try {
        const { App } = await import("@capacitor/app");

        handles.push(
          await App.addListener("appStateChange", ({ isActive }) => {
            if (!isActive) {
              rememberCurrentNativeRoute();
            } else if (window.location.pathname === "/") {
              const remembered = getRememberedNativeRoute();
              if (remembered && remembered !== "/") {
                router.replace(remembered);
              }
            }
            window.dispatchEvent(
              new CustomEvent("sproutly-native-app-state", {
                detail: { isActive },
              })
            );
          })
        );

        handles.push(
          await App.addListener("appUrlOpen", ({ url }) => {
            routeNativePath(url);
          })
        );

        handles.push(
          await App.addListener("backButton", ({ canGoBack }) => {
            if (canGoBack) {
              window.history.back();
            } else {
              App.minimizeApp();
            }
          })
        );

        const launchUrl = await App.getLaunchUrl();
        if (!cancelled && launchUrl?.url) {
          routeNativePath(launchUrl.url);
        }
      } catch (err) {
        console.warn("[native-bridge] setup failed", err);
      }
    })();

    return () => {
      cancelled = true;
      handles.forEach((handle) => {
        try {
          handle?.remove?.();
        } catch {}
      });
    };
  }, [router]);

  return null;
}
